<template>
    <v-form ref="form" v-model="valid" class="pb-4">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col v-if="formData.hasOwnProperty('active')" cols="12" sm="6" md="6" class="align-center">
                <div class="width-max-content ml-auto">
                    <v-switch
                        v-model="formData.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="formData.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                        :disabled="isDisabled"
                        :readonly="isHotelDescriptionMode"
                    />
                </div>
            </v-col>
        </v-row>
        <slot name="top-block" />
        <v-row v-if="formData.hasOwnProperty('name')" dense>
            <v-col cols="12" md="4">
                <TextFieldLocale
                    v-model="formData.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name')]"
                    :readonly="isHotelDescriptionMode || isNameFieldReadonly"
                />
            </v-col>
            <!--
                UI-36: disclaimer next to the Name box — Meal-type form only, always
                shown (new + edit, any meal plan incl. Room Only). Gated on
                `useAsDefault`, NOT `mealCode`: useAsDefault is unique to
                initInfoMealTypes (ProductHotelMealTypeForm) and is a boolean, so it
                survives save — whereas mealCode is null-stripped from the backend
                response after save, which hid this notice on reopen.
            -->
            <v-col v-if="formData.hasOwnProperty('useAsDefault')" cols="12" md="8">
                <div class="ui36-name-disclaimer font-weight-bold">
                    <v-icon icon="mdi-information-outline" size="18" start />
                    {{ $t('product.info.name_visible_to_guests') }}
                </div>
            </v-col>
            <slot name="name-block" />
        </v-row>
        <slot name="center-block" />
        <v-row v-if="false && formData.hasOwnProperty('description') && isShowEditors" dense>
            <v-col cols="12" md="8" class="mb-4">
                <div class="text-subtitle-1">{{ $t('description') }}:</div>
                <NewHtmlEditor
                    v-model="formData.description[activeLangCode]"
                    :toolbar="isHotelDescriptionMode ? {items: []} : undefined"
                    class="mb-2"
                >
                    <div
                        v-if="activeLangCode"
                        class="fi fill-height px-3"
                        :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                    />
                </NewHtmlEditor>
            </v-col>
        </v-row>
        <slot name="bottom-block" />
        <v-row no-gutters class="gc-4">
            <v-btn
                v-if="!isHotelDescriptionMode"
                data-cy="save"
                :disabled="isLoading || !valid || isShowValidationError"
                @click="emit('save')"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn class="cancel-btn" data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {useFlagIconCode} from '~/composables/helpers'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {nextTick} from 'vue'

    const NewHtmlEditor = defineAsyncComponent(() => import('~/components/admin/NewHtmlEditor.vue'))

    const props = defineProps<{
        isLoading?: boolean
        isDisabled?: boolean
        isShowValidationError?: boolean
        isNameFieldReadonly?: boolean
    }>()

    const formData = defineModel<any>()
    const emit = defineEmits<{
        (e: 'save'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'cancel'): void
    }>()

    watch(
        () => formData.value,
        () => {
            formData.value.description = {...description.value, ...formData.value.description}
            flickEditors()
        }
    )

    const valid = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const isShowEditors = ref(true)
    const description = ref({})
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)

    const app = useNuxtApp()

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        description.value = Object.fromEntries(useLocales.value.map(lang => [lang, '']))
        formData.value.description = {...description.value, ...formData.value.description}
    })

    const useLocales = computed(() => {
        return Object.values(app.$conf.public.locales)
    })

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
        flickEditors()
    }

    const flickEditors = (): void => {
        isShowEditors.value = false
        nextTick().then(() => {
            isShowEditors.value = true
        })
    }

    defineExpose({
        isShowEditors,
        activeLangCode,
    })
</script>

<style scoped lang="scss">
    // UI-36: light teal-blue "note" box for the guest-facing-name disclaimer.
    // Tokens mirror zsh-frontend: bg tint of the primary, -400 accent, -600 text.
    .ui36-name-disclaimer {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 6px;
        background-color: #e7f3f4;
        border-left: 3px solid #51b5bf; // $color-primary-bg-400
        color: #2d7784; // $color-primary-bg-600
        font-size: 13px;
        line-height: 1.35;

        .v-icon {
            color: #51b5bf;
            flex-shrink: 0;
            opacity: 1;
        }
    }
</style>
